import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  LinearProgress,
  Chip,
  Button,
  Divider,
} from "@mui/material";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
//import ReportService from "../../services/ReportService";
import { useReportDataService } from "../../services/ReportDataService";

const columnDefs = [
  { key: "Business_Vertical_Desc", label: "Business Vertical", flex: 2 },
  { key: "Group_Employee_Code", label: "Employee Code", flex: 1.5 },
  { key: "Full_Name", label: "Full Name", flex: 2 },
  { key: "SuccessfulAttempts", label: "Success", flex: 1 },
  { key: "FailedAttempts", label: "Failed", flex: 1 },
];

const ROW_HEIGHT = 32;

const FaceValidationReport = ({ empID = "All" }) => {
  const { fetchValidationLog } = useReportDataService();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState({});
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());

  const listRef = useRef();
  //const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [sortRules, setSortRules] = useState([]); // [{ key: "SuccessfulAttempts", direction: "asc" }]

 const fetchData = useCallback(async () => {
  setLoading(true);
  try {
    const params = {
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
      employeeID: empID || "All"
    };
    const response = await fetchValidationLog(params);
    console.log(response);
    setData(response || []);
  } catch (err) {
    console.error("Error fetching face validation data", err);
    setData([]);
  } finally {
    setLoading(false);
  }
}, [startDate, endDate, empID, fetchValidationLog]);


useEffect(() => {
  fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


  const handleSort = (key) => {
    setSortRules((prev) => {
      const existing = prev.find((rule) => rule.key === key);
      if (existing) {
        // Toggle direction or remove if already descending
        const newDirection = existing.direction === "asc" ? "desc" : null;
        if (newDirection === null) {
          return prev.filter((rule) => rule.key !== key);
        }
        return prev.map((rule) =>
          rule.key === key ? { ...rule, direction: newDirection } : rule
        );
      } else {
        // Add new sort key at the end
        return [...prev, { key, direction: "asc" }];
      }
    });
  };

  const filteredData = useMemo(() => {
    let tempData = data.filter((item) =>
      columnDefs.every(({ key }) => {
        const term = search[key]?.toLowerCase() || "";
        return item[key]?.toString().toLowerCase().includes(term);
      })
    );

    if (sortRules.length > 0) {
      tempData = [...tempData].sort((a, b) => {
        for (const { key, direction } of sortRules) {
          const valA = a[key];
          const valB = b[key];
          if (valA !== valB) {
            return direction === "asc" ? valA - valB : valB - valA;
          }
        }
        return 0;
      });
    }

    return tempData;
  }, [data, search, sortRules]);

  const Row = ({ index, style }) => {
    const row = filteredData[index];
    return (
      <Box
        style={style}
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #eee",
          px: 0.8,
          fontSize: "0.775rem",
          lineHeight: 1.4,
          minHeight: ROW_HEIGHT,
        }}
      >
        {columnDefs.map(({ key, flex }) => (
          <Box
            key={key}
            sx={{
              flex,
              minWidth: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              pr: 1,
            }}
          >
            {typeof row[key] === "number" ? (
              row[key]
            ) : ["Yes", "No"].includes(row[key]) ? (
              <Chip
                label={row[key]}
                size="small"
                sx={{ fontSize: "0.75rem", height: 24 }}
                color={row[key] === "Yes" ? "success" : "error"}
              />
            ) : (
              row[key] || "-"
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FaceValidationReport");
    XLSX.writeFile(wb, "FaceValidationReport.xlsx");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 1 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Typography variant="h2">Face Validation Report</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <DesktopDatePicker
              label="Start Date"
              inputFormat="DD/MM/YYYY"
              value={startDate}
              onChange={setStartDate}
              renderInput={(params) => <TextField size="small" {...params} />}
            />
            <DesktopDatePicker
              label="End Date"
              inputFormat="DD/MM/YYYY"
              value={endDate}
              onChange={setEndDate}
              renderInput={(params) => <TextField size="small" {...params} />}
            />
            <Button variant="contained" onClick={fetchData}>
              Fetch
            </Button>
            <Button
              variant="contained" 
              onClick={handleExport}
            >
              Export to Excel
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {loading ? (
          <LinearProgress />
        ) : (
          <Box component={Paper}>
            {/* Header Row */}
            <Box sx={{ display: "flex", p: 1, borderBottom: "1px solid #ccc" }}>
              {columnDefs.map(({ key, label, flex }) => (
                <Box key={key} sx={{ flex, pr: 1, minWidth: 0 }}>
                  {["SuccessfulAttempts", "FailedAttempts"].includes(key) ? (
                    <Button
                      onClick={() => handleSort(key)}
                      size="small"
                      sx={{ textTransform: "none", fontWeight: "bold" }}
                    >
                      {label}
                      {(() => {
                        const sort = sortRules.find((rule) => rule.key === key);
                        if (!sort) return "";
                        return sort.direction === "asc" ? " ▲" : " ▼";
                      })()}
                    </Button>
                  ) : (
                    <TextField
                      label={label}
                      variant="standard"
                      value={search[key] || ""}
                      onChange={(e) =>
                        setSearch((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                      fullWidth
                      size="small"
                    />
                  )}
                </Box>
              ))}
              <Box sx={{ width: 32 }} />
            </Box>

            <Box sx={{ height: "65vh" }}>
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    height={height}
                    width={width}
                    itemCount={filteredData.length}
                    itemSize={() => ROW_HEIGHT}
                    itemKey={(index) =>
                      filteredData[index].Group_Employee_Code || index
                    }
                    ref={listRef}
                  >
                    {Row}
                  </List>
                )}
              </AutoSizer>
            </Box>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default FaceValidationReport;
